import dynamic from "next/dynamic";

import React, { useMemo } from "react";

import { Entity } from "models/entity";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { SKSpinner } from "components/customElements/skSpinner";
import EmptyState from "components/EmptyState";
import { useApi } from "hooks/useApi";
import { baseFetcher } from "utils/baseFetcher";

import { consensusTargetUrl, isDataEmpty } from "../utils";

const AvgRatingChart = dynamic(() => import("./AvgRatingChart"), {
  ssr: false,
});

interface ConsensusRatingsProps {
  entity: Entity;
}

const ConsensusRatings = ({ entity }: ConsensusRatingsProps) => {
  const { data: consensusTarget, isValidating: isValidatingConsensusTarget } =
    useApi(consensusTargetUrl(entity.id), baseFetcher, {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    });

  const {
    fest_est_ar_avg_no,
    fest_est_ar_strongbuy,
    fest_est_ar_outperform,
    fest_est_ar_hold,
    fest_est_ar_sell,
    fest_est_ar_underperform,
  } = consensusTarget || {};

  const maxConsensusTargetValue = useMemo(
    () =>
      Math.max(
        fest_est_ar_strongbuy?.value || 0,
        fest_est_ar_outperform?.value || 0,
        fest_est_ar_hold?.value || 0,
        fest_est_ar_sell?.value || 0,
        fest_est_ar_underperform?.value || 0
      ),
    [consensusTarget]
  );

  const range = 5;
  const gap = Math.ceil(
    maxConsensusTargetValue ? maxConsensusTargetValue / 5 : 1
  );
  const max = gap * 5;

  const ratings = useMemo(() => {
    return [
      {
        name: "Strong Buy",
        value: fest_est_ar_strongbuy?.value || 0,
        colorClassName: "bg-bullish-green",
      },
      {
        name: "Buy",
        value: fest_est_ar_outperform?.value || 0,
        colorClassName: "bg-bullish-green",
      },
      {
        name: "Hold",
        value: fest_est_ar_hold?.value || 0,
        colorClassName: "bg-shady-gray",
      },
      {
        name: "Sell",
        value: fest_est_ar_underperform?.value || 0,
        colorClassName: "bg-bearish-red",
      },
      {
        name: "Strong Sell",
        value: fest_est_ar_sell?.value || 0,
        colorClassName: "bg-bearish-red",
      },
    ];
  }, [
    fest_est_ar_strongbuy,
    fest_est_ar_outperform,
    fest_est_ar_hold,
    fest_est_ar_underperform,
    fest_est_ar_sell,
  ]);

  if (
    !isValidatingConsensusTarget &&
    isDataEmpty(consensusTarget, [
      "fest_est_ar_strongbuy",
      "fest_est_ar_outperform",
      "fest_est_ar_hold",
      "fest_est_ar_underperform",
      "fest_est_ar_sell",
    ])
  ) {
    return (
      <CustomCard className="flex-1 p-[15px]">
        <div
          className={twMerge(
            "flex-1",
            "pb-[10px]",
            "font-medium",
            "text-[16px]",
            "text-[#1f1f20]"
          )}
        >
          Consensus Ratings
        </div>
        <EmptyState
          type="data"
          className="pt-[50px] md:pt-[50px] pb-[50px] md:pb-[50px]"
        />
      </CustomCard>
    );
  }

  return (
    <CustomCard className="flex-1 p-[15px] shadow-[0_2px_15px_rgba(0,0,0,0.15)]">
      <div className="flex">
        <div
          className={twMerge(
            "flex-1",
            "pb-[10px]",
            "font-medium",
            "text-[16px]",
            "text-[#1f1f20]"
          )}
        >
          Consensus Ratings
        </div>
        <div className="font-bold">
          {numeral(fest_est_ar_avg_no?.value).format("0,0.00")}/5
        </div>
      </div>
      {isValidatingConsensusTarget ? (
        <div className="w-full flex justify-center">
          <SKSpinner />
        </div>
      ) : (
        <div>
          <div className="flex mb-[30px] text-[13px]">
            <div className="flex-1">Number of Covering Analysts</div>
            <div className="font-bold">
              {(fest_est_ar_strongbuy?.value || 0) +
                (fest_est_ar_outperform?.value || 0) +
                (fest_est_ar_hold?.value || 0) +
                (fest_est_ar_sell?.value || 0) +
                (fest_est_ar_underperform?.value || 0)}
            </div>
          </div>
          <AvgRatingChart entity={entity} />

          <div className="mt-[6px] pr-[20px] pb-[30px] space-y-[16px]">
            <div className="flex">
              <div className="min-w-[100px]" />
              <div className="flex-1 relative ml-[3px]">
                {Array.from(Array(range + 1).keys()).map((index) => {
                  const left = `${(index / range) * 100}%`;

                  return (
                    <div
                      key={index}
                      className="absolute ml-[-3px] border border-solid border-[#F5F5F5] w-0 z-0"
                      style={{
                        left,
                        top: 25,
                        height: 175,
                      }}
                    ></div>
                  );
                })}
              </div>
            </div>
            {ratings.map(({ name, value, colorClassName }) => (
              <div key={name} className="flex z-1">
                <div className="min-w-[100px]">
                  <span className="cursor-pointer">{name}</span>
                </div>
                <div className="flex-1 pt-[8px] drop-shadow-lg">
                  <div
                    className={twMerge(
                      colorClassName,
                      "rounded-full h-[5px]",
                      "relative"
                    )}
                    style={{
                      width: `${(value / (max || 1)) * 100}%`,
                    }}
                  >
                    <div
                      className={twMerge(
                        "absolute top-[-5px]",
                        value > 0 ? "right-[-6px]" : "left-[-6px]",
                        colorClassName,
                        "w-[15px] h-[15px] rounded-full",
                        "text-white text-[10px] text-center leading-[16px]"
                      )}
                    >
                      {value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex">
              <div className="min-w-[100px]" />
              <div className="flex-1 relative mx-[2px]">
                {Array.from(Array(range + 1).keys()).map((index) => {
                  const left = `${(index / range) * 100}%`;

                  return (
                    <div
                      key={index}
                      className="absolute ml-[-3px] text-intelligent-silver"
                      style={{
                        left,
                      }}
                    >
                      {index * gap}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </CustomCard>
  );
};

export default ConsensusRatings;
